import type { Track } from "./telemetry.js";

/**
 * `client_error` (docs/metrics.md): every uncaught error or unhandled rejection, counted in our own
 * events wherever the page runs (crash reports go to Sentry only where it's set up). Nothing that
 * could be personal: the error's class and a hash of its stack, with URLs left out.
 */

/**
 * The play on this page (GameHost.start → stop), as every play reports it (playProps: game,
 * version, mission, mode), or null: an error during a play names its game (P3-OBS-02).
 */
let play: Readonly<Record<string, string>> | null = null;
export const setPlay = (next: Readonly<Record<string, string>> | null) => {
  play = next;
};

/** At most this many per page load: a page throwing in a loop is one problem, not a thousand. */
const MAX_PER_PAGE = 5;

/**
 * A short, stable hash of a stack's frames: function names and positions, without URLs, query
 * strings or messages (FNV-1a, 32 bits, hex). The same crash hashes the same everywhere.
 */
export function stackHash(stack: string | undefined): string {
  const frames = (stack ?? "")
    .split("\n")
    .slice(1, 8)
    .map((line) =>
      line
        .replace(/https?:\/\/[^\s)]+\/([^/\s)]+)/g, "$1")
        .replace(/\?[^\s:)]*/g, "")
        .trim(),
    )
    .join("|");
  let h = 0x811c9dc5;
  for (let i = 0; i < frames.length; i++) {
    h ^= frames.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** The properties for one error. Fatal: during a match, thrown from the game's own bundle. */
export function describe(error: unknown, duringMatch: boolean) {
  const err = error instanceof Error ? error : null;
  const stack = err?.stack;
  return {
    fatal: duringMatch && /\/games\//.test(stack ?? ""),
    during_match: duringMatch,
    error_class: err ? err.name.slice(0, 40) : typeof error,
    stack_hash: stackHash(stack),
  };
}

/** Starts counting: listeners for uncaught errors and unhandled rejections. */
export function countClientErrors(track: Track, target: EventTarget = window): void {
  let sent = 0;
  const report = (error: unknown) => {
    if (sent >= MAX_PER_PAGE) return;
    sent++;
    track("client_error", { ...describe(error, play !== null), ...play });
  };
  target.addEventListener("error", (e) => {
    report((e as ErrorEvent).error ?? null);
  });
  target.addEventListener("unhandledrejection", (e) => {
    report((e as PromiseRejectionEvent).reason);
  });
}
